import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import HesaSignalsStrip from "./HesaSignalsStrip";

/**
 * DirectoryMatchesSection
 * -----------------------------------------------------------------------------
 * Renders a collapsible strip beneath the AdvancedResults 5-layer editorial
 * matches: "Also in the directory (N) — not editorially reviewed". Fetches
 * directory-only stubs matching the user's `intended_subject` (free-text
 * regex on official_title) and optionally destination country.
 *
 * Deliberately quieter than editorial ResultCards — one-line rows, no chance
 * score, no cost delta. Each row links to the university detail page with a
 * `?open=directory` query flag so the user lands on that uni's full course
 * list. The section stays collapsed by default so it doesn't visually
 * outweigh the editorial matches above.
 */
export default function DirectoryMatchesSection({ subject, country }) {
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(null);
  const [items, setItems] = useState([]);
  const [chances, setChances] = useState({});   // { [degree_id]: {band, chance_pct, label} }

  useEffect(() => {
    if (!subject) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          directory_only: "true",
          title: subject,
          limit: "20",
          view: "directory",
        });
        if (country) params.set("country", country);
        const { data, headers } = await api.get(`/degrees?${params}`);
        if (cancelled) return;
        setItems(data);
        setTotal(parseInt(headers["x-total-count"] || "0", 10));

        // Fire-and-forget chance-estimate. If the user isn't auth'd (401)
        // we simply skip the badges — the section still works without them.
        if (data.length > 0) {
          try {
            const est = await api.post("/directory/chance-estimate", {
              degree_ids: data.map((d) => d.id),
            });
            if (!cancelled) setChances(est.data || {});
          } catch { /* silent — badges just won't render */ }
        }
      } catch {
        if (!cancelled) { setItems([]); setTotal(0); }
      }
    })();
    return () => { cancelled = true; };
  }, [subject, country]);

  if (!subject) return null;
  if (total === null) return null;
  if (total === 0) return null;

  return (
    <section
      data-testid="advresults-directory-section"
      className="mt-10 border-t border-stone-200 pt-8"
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="advresults-directory-toggle"
        className="w-full flex items-center justify-between gap-4 py-2 hover:bg-stone-50 transition-colors rounded"
      >
        <div className="text-left">
          <div className="label-uppercase !text-[10px] text-stone-500">
            Also available — Directory, not editorially reviewed
          </div>
          <h3 className="font-editorial text-xl mt-1">
            {total.toLocaleString()} more course{total === 1 ? "" : "s"} match "{subject}"
            {country ? ` in ${country}` : ""}
          </h3>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-stone-500 shrink-0" /> : <ChevronDown className="h-5 w-5 text-stone-500 shrink-0" />}
      </button>

      {open && (
        <div className="mt-4">
          <p className="text-[11px] text-stone-500 max-w-2xl">
            These are official-data course records we haven't yet written up in full
            editorial depth. We show a <span className="font-semibold">chance-estimate</span> based on
            the university's typical entry requirements — request a full review to unlock
            warnings, cost breakdown and student reviews.
          </p>
          <div className="mt-4 divide-y divide-stone-100 border border-stone-200 rounded-md bg-white">
            {items.map((it) => {
              const est = chances[it.id];
              const bandCls =
                est?.band === "safe"    ? "border-emerald-300 bg-emerald-50 text-emerald-800" :
                est?.band === "target"  ? "border-amber-300 bg-amber-50 text-amber-800" :
                est?.band === "reach"   ? "border-rose-300 bg-rose-50 text-rose-800" :
                                          "border-stone-300 bg-stone-50 text-stone-700";
              return (
                <Link
                  key={it.id}
                  to={`/uni/${it.university_id}?open=directory`}
                  data-testid={`advresults-directory-row-${it.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-stone-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-stone-900 truncate">
                      {it.official_title}
                    </div>
                    <div className="mt-0.5 text-[11px] text-stone-500 font-underground-sc tracking-wider">
                      {it.qualification || "—"} · {it.duration_years || 3} yr · {it.university_id.replace(/^([a-z]{2})-/, "").replace(/-/g, " ")}
                    </div>
                    <HesaSignalsStrip stats={it.hesa_stats} testIdPrefix={`advresults-directory-${it.id}`} />
                  </div>
                  {est && (
                    <span
                      data-testid={`advresults-directory-chance-${it.id}`}
                      title={est.reason}
                      className={`shrink-0 text-[10px] font-medium border rounded-full px-2 py-0.5 ${bandCls}`}
                    >
                      {est.label || est.band}
                      <span className="ml-1 text-[9px] font-underground-sc opacity-60">rule-based</span>
                    </span>
                  )}
                  <span className="text-[10px] text-stone-400 font-underground-sc tracking-wider shrink-0">Directory ›</span>
                </Link>
              );
            })}
          </div>
          {total > items.length && (
            <p className="mt-3 text-[11px] text-stone-500">
              Showing the first {items.length} of {total.toLocaleString()}. Open a university above to see its full course list.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
